<script setup lang="ts">
/**
 * One meal's readiness, on one screen (SPEC §6.22, §8.13).
 *
 * SPEC §6.22 is explicit that an organizer must understand whether a meal is
 * ready without opening several pages, so everything that decides that is above
 * the fold: who is coming, who has not said, and what nobody has offered to
 * bring. The panels below are detail; the numbers at the top are the answer.
 *
 * Four requests rather than one. This is a planning screen an organizer opens
 * on a laptop with time to spare — the one-round-trip rule belongs to the
 * family surface, which is opened outdoors on a phone (SPEC §22.1).
 */
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import EmptyState from '@/components/EmptyState.vue'
import ChangeNoticePanel from '@/components/ChangeNoticePanel.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import EventReminderSchedule from '@/components/EventReminderSchedule.vue'
import MealDetailsDialog from '@/components/MealDetailsDialog.vue'
import ReminderComposer from '@/components/ReminderComposer.vue'
import ShareLinkCard from '@/components/ShareLinkCard.vue'
import {
  ApiError,
  endpoints,
  type AttendanceSummaryResponse,
  type EventInvitationSetResponse,
  type EventMenuResponse,
  type MealEventResponse,
  type ReminderSentResponse,
} from '@/api'
import { asCount, asNumber } from '@/utils/numbers'
import { describeWhen, whenLine } from '@/utils/dates'

const route = useRoute()
const router = useRouter()

const groupId = computed(() => String(route.params.groupId))
const eventId = computed(() => String(route.params.eventId))

const meal = ref<MealEventResponse | null>(null)
const menu = ref<EventMenuResponse | null>(null)
const attendance = ref<AttendanceSummaryResponse | null>(null)
const invitations = ref<EventInvitationSetResponse | null>(null)
const reminders = ref<ReminderSentResponse[]>([])

const loading = ref(true)
const error = ref<string | null>(null)
const message = ref<string | null>(null)

const sharing = ref(false)
const reminding = ref(false)

/** Only a published meal has a link to share or anybody to remind. */
const published = computed(() => meal.value?.status === 'Published')

const when = computed(() =>
  meal.value ? whenLine(describeWhen(meal.value.localDate, meal.value.localTime)) : '',
)

/**
 * Items nobody has taken.
 *
 * Counted from the menu rather than asked for separately, because the menu is
 * already on screen and a second number computed a second way is a second
 * number to disagree with the first.
 */
const unclaimed = computed(() => {
  if (!menu.value) return []

  return menu.value.sections
    .flatMap((section) => section.items)
    .filter(
      (item) =>
        item.participationMode === 'SignupRequired' && (asNumber(item.remainingQuantity) ?? 1) > 0,
    )
})

const unanswered = computed(() =>
  (invitations.value?.invited ?? []).filter((row) => row.rsvpStatus === 'Unanswered'),
)

/**
 * The prompt SPEC §6.13.1 asks for.
 *
 * Only households that joined after publication. One taken off the meal on
 * purpose is not somebody to offer to re-add.
 */
const joinedSince = computed(() =>
  (invitations.value?.notInvited ?? []).filter((row) => row.joinedAfterPublication),
)

/** The headline numbers from SPEC §8.13, in the order it lists them. */
const metrics = computed(() => {
  const summary = attendance.value

  return [
    { label: 'Attending', value: asCount(summary?.people), testid: 'metric-attending' },
    { label: 'Adults', value: asCount(summary?.adults), testid: 'metric-adults' },
    { label: 'Kids', value: asCount(summary?.children), testid: 'metric-kids' },
    { label: 'Awaiting RSVP', value: unanswered.value.length, testid: 'metric-unanswered' },
    { label: 'Still needed', value: unclaimed.value.length, testid: 'metric-unclaimed' },
  ]
})

/** How each household answered, grouped the way an organizer reads it. */
const replies = computed(() => {
  const invited = invitations.value?.invited ?? []

  return (['Yes', 'Maybe', 'No', 'Unanswered'] as const).map((status) => ({
    status,
    households: invited.filter((row) => row.rsvpStatus === status),
  }))
})

function describe(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 404) {
    return 'We cannot find that meal. It may have been removed.'
  }

  return (caught instanceof ApiError && caught.problem?.title) || 'That did not load.'
}

/*
  Publishing, and changing the details (MT-163).

  Publishing asks first, because it is the moment families are invited and it
  cannot be taken back — only cancelled. Changing the details asks afterwards,
  and only when the change is one a family would need to know about.
*/
const confirmingPublish = ref(false)
const publishing = ref(false)
const editingDetails = ref(false)
const changeNotice = ref<string[] | null>(null)

async function publish(): Promise<void> {
  publishing.value = true
  error.value = null

  try {
    await endpoints.publishMealEvent(groupId.value, eventId.value)
    confirmingPublish.value = false
    await load()
  } catch (caught) {
    confirmingPublish.value = false
    error.value = describe(caught)
  } finally {
    publishing.value = false
  }
}

function detailsSaved(updated: NonNullable<typeof meal.value>, changes: string[]): void {
  meal.value = updated

  // Only a published meal has anybody to tell.
  if (updated.status === 'Published' && changes.length > 0) {
    changeNotice.value = changes
  }
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [event, currentMenu, totals, set] = await Promise.all([
      endpoints.getMealEvent(groupId.value, eventId.value),
      endpoints.getEventMenu(groupId.value, eventId.value),
      endpoints.eventAttendance(groupId.value, eventId.value),
      endpoints.eventInvitations(groupId.value, eventId.value),
    ])

    meal.value = event
    menu.value = currentMenu
    attendance.value = totals
    invitations.value = set
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }

  await loadReminders()
}

/**
 * Loaded separately, and allowed to fail quietly.
 *
 * Reminder history is the least important panel on the screen. A meal's
 * readiness should not be unreadable because one audit query did not answer.
 */
async function loadReminders() {
  try {
    reminders.value = await endpoints.eventReminders(groupId.value, eventId.value)
  } catch {
    reminders.value = []
  }
}

function editMenu() {
  void router.push({
    name: 'organizer-event-menu',
    params: { groupId: groupId.value, eventId: eventId.value },
  })
}

function back() {
  void router.push({ name: 'organizer-events', params: { groupId: groupId.value } })
}

function sent() {
  message.value = 'Reminder copied. Paste it wherever your family talks.'
  void loadReminders()
}

void load()
</script>

<template>
  <div class="dash">
    <header class="dash__head">
      <v-btn variant="text" density="comfortable" prepend-icon="mdi-arrow-left" @click="back">
        Meals
      </v-btn>
    </header>

    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>
    <v-alert v-if="message" type="success" variant="tonal">{{ message }}</v-alert>

    <v-skeleton-loader v-if="loading" type="article" />

    <template v-else-if="meal">
      <div class="dash__title">
        <h1 class="dash__name">{{ meal.title }}</h1>
        <p class="dash__when">
          {{ when }}
          <v-chip size="small" variant="tonal" class="ml-2" data-testid="status">
            {{ meal.status }}
          </v-chip>
        </p>
        <p v-if="meal.locationName" class="dash__where">{{ meal.locationName }}</p>
      </div>

      <!--
        Above the fold, and the whole point of the screen: whether this meal is
        ready (SPEC §6.22).
      -->
      <ul class="dash__metrics">
        <li v-for="metric in metrics" :key="metric.label" class="dash__metric">
          <span class="dash__metric-value" :data-testid="metric.testid">{{ metric.value }}</span>
          <span class="dash__metric-label">{{ metric.label }}</span>
        </li>
      </ul>

      <!--
        A projection is not a fact. A draft has no invited set, so what the
        numbers above show is what they would be if it were published now, and
        saying so is the difference between a number and a guess (SPEC §6.13.1).
      -->
      <v-alert
        v-if="attendance?.isProjection"
        type="info"
        variant="tonal"
        density="compact"
        data-testid="projection"
      >
        This meal has not been published, so nobody has been asked yet. These numbers are what they
        would be if you published it now.
      </v-alert>

      <div class="dash__actions">
        <v-btn
          v-if="meal.status === 'Draft'"
          color="primary"
          variant="flat"
          data-testid="publish"
          @click="confirmingPublish = true"
        >
          Publish
        </v-btn>
        <v-btn
          v-if="meal.status === 'Draft' || meal.status === 'Published'"
          variant="outlined"
          data-testid="edit-details"
          @click="editingDetails = true"
        >
          Edit details
        </v-btn>
        <v-btn variant="outlined" @click="editMenu">Edit menu</v-btn>
        <v-btn v-if="published" variant="outlined" data-testid="remind" @click="reminding = true">
          Send reminder
        </v-btn>
        <v-btn v-if="published" variant="outlined" data-testid="share" @click="sharing = true">
          Share
        </v-btn>
      </div>

      <v-alert
        v-if="joinedSince.length > 0"
        type="info"
        variant="tonal"
        density="compact"
        data-testid="joined-since"
      >
        {{ joinedSince.length }}
        {{ joinedSince.length === 1 ? 'household has' : 'households have' }} joined since you
        published this: {{ joinedSince.map((h) => h.householdName).join(', ') }}.
      </v-alert>

      <section class="dash__panel">
        <h2 class="dash__panel-name">Who is coming</h2>
        <div v-for="group in replies" :key="group.status" class="dash__group">
          <p class="dash__group-name">
            {{ group.status === 'Unanswered' ? 'No answer yet' : group.status }} ·
            {{ group.households.length }}
          </p>
          <p v-if="group.households.length > 0" class="dash__group-names">
            {{ group.households.map((h) => h.householdName).join(' · ') }}
          </p>
        </div>
      </section>

      <section class="dash__panel">
        <h2 class="dash__panel-name">Still needed</h2>
        <EmptyState
          v-if="unclaimed.length === 0"
          compact
          icon="mdi-check-circle-outline"
          headline="Nothing still needed"
          testid="nothing-needed"
          description="Everything on the menu is covered."
        />
        <ul v-else class="dash__list">
          <li v-for="item in unclaimed" :key="item.id" class="dash__row">
            <span>{{ item.name }}</span>
            <span class="dash__row-meta">
              {{ asNumber(item.remainingQuantity) ?? 1 }} still needed
            </span>
          </li>
        </ul>
      </section>

      <section v-if="meal" class="dash__panel">
        <h2 class="dash__panel-name">Scheduled reminders</h2>
        <EventReminderSchedule
          :group-id="groupId"
          :event-id="eventId"
          :time-zone="meal.timeZoneId"
        />
      </section>

      <section class="dash__panel">
        <h2 class="dash__panel-name">Reminders</h2>
        <EmptyState
          v-if="reminders.length === 0"
          compact
          icon="mdi-message-outline"
          headline="No reminders sent"
          testid="no-reminders"
          description="Nothing sent yet."
        />
        <ul v-else class="dash__list">
          <li v-for="(sentReminder, index) in reminders" :key="index" class="dash__row">
            <span>{{ sentReminder.audience }} · {{ sentReminder.kind }}</span>
            <span class="dash__row-meta">
              {{ sentReminder.householdCount }}
              {{ sentReminder.householdCount === 1 ? 'household' : 'households' }} ·
              {{ sentReminder.method === 'Manual' ? 'sent by you' : 'sent for you' }}
            </span>
          </li>
        </ul>
      </section>

      <ConfirmDialog
        v-model="confirmingPublish"
        title="Publish this meal?"
        body="Every household in the group is invited and can start answering. It cannot be unpublished, only cancelled."
        confirm-label="Publish"
        cancel-label="Not yet"
        :busy="publishing"
        @confirm="publish"
      />

      <MealDetailsDialog
        v-model="editingDetails"
        :group-id="groupId"
        :meal="meal"
        @saved="detailsSaved"
      />

      <v-dialog
        :model-value="changeNotice !== null"
        max-width="32rem"
        persistent
        data-testid="change-notice-dialog"
      >
        <v-card>
          <v-card-text>
            <ChangeNoticePanel
              v-if="changeNotice"
              :group-id="groupId"
              :event-id="eventId"
              kind="Changed"
              :changes="changeNotice"
              @done="changeNotice = null"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="reminding" max-width="32rem">
        <v-card>
          <v-card-title tag="h2">Send a reminder</v-card-title>
          <v-card-text>
            <p class="dash__lead">
              We will write it. You send it in whatever your family already uses.
            </p>

            <ReminderComposer
              :group-id="groupId"
              :event-id="eventId"
              :open="reminding"
              @sent="sent"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="reminding = false">Done</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="sharing" max-width="32rem">
        <v-card>
          <v-card-title tag="h2">Share this meal</v-card-title>
          <v-card-text>
            <p class="dash__lead">
              One link for the whole thread. Whoever taps it and already has their family's page
              open lands straight on it; everybody else sees the meal and a way to find theirs.
            </p>

            <ShareLinkCard
              :group-id="groupId"
              :event-id="eventId"
              :title="meal.title"
              :when="when"
              :open="sharing"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="sharing = false">Done</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<style scoped>
.dash {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dash__title {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.dash__name {
  font-size: 1.75rem;
  line-height: 1.2;
  margin: 0;
}

.dash__when {
  margin: 0;
}

.dash__where,
.dash__empty,
.dash__group-names,
.dash__row-meta {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.875rem;
}

/*
  Wraps rather than scrolls. Five numbers across a 360px phone is three on the
  first line and two on the second, which is readable; a horizontal scroller
  hides the ones that matter (SPEC §8.2).
*/
.dash__metrics {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.dash__metric {
  display: flex;
  flex-direction: column;
}

.dash__metric-value {
  font-size: 1.75rem;
  line-height: 1.1;
}

.dash__metric-label {
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-on-surface-variant));
}

.dash__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.dash__actions .v-btn {
  min-height: 2.75rem;
}

.dash__panel {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.dash__panel-name {
  font-size: 0.8125rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-on-surface-variant));
  margin: 0.5rem 0 0.25rem;
}

.dash__group {
  display: flex;
  flex-direction: column;
}

.dash__group-name {
  margin: 0;
  font-weight: 600;
}

.dash__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.dash__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
}

.dash__row:last-child {
  border-bottom: 0;
}

.dash__lead {
  margin: 0 0 1rem;
  color: rgb(var(--v-theme-on-surface-variant));
}
</style>
